import { useState } from "react";
import { useIntl, type IntlShape } from "react-intl";
import type { AvatarRejection, CropResult } from "@/components/common/AvatarCropper";
import type { SystemSettingsFileInput } from "@/graphql/generated/operations";
import { acceptsFile } from "@/lib/file-ingest";
import { BRAND_IMAGE_TOO_LARGE, BRAND_IMAGE_UNSUPPORTED } from "@/lib/genesis-error-codes";
import { graphQlErrorExtensions } from "@/lib/graphql-error";
import { imageDataUrl, type ImageValue } from "@/lib/image-data-url";
import {
  MAX_AVATAR_PIXELS,
  MAX_BRAND_LOGO_BYTES,
  MB,
  RASTER_IMAGE_ACCEPT,
} from "@/lib/upload-limits";

// Choosing, framing, replacing and removing the platform logo from the Branding panel (spark#314).
//
// THE SIBLING OF use-company-logo-editing, NOT A COPY OF IT. Both go through the same picker and the
// same cropper; what differs is what happens to the result:
//
//   - The logo is ONE FIELD OF THE SETTINGS PATCH. genesis merges `updateSystemSettings` into the
//     stored row, so a patch that carries `logo` writes it and a patch that omits it leaves it be
//     (SystemSettingsController: settings.putAll). This hook therefore hands the panel a patch
//     value, `undefined` for "untouched", and the panel folds it into the one save the SaveBar
//     drives, next to the app name and the palette.
//   - A CHOSEN FILE IS NOT A PICK YET: IT IS FRAMED FIRST, in the cropper's wide frame (4:1, the
//     sidebar's own proportion, PNG so a transparent background survives). Only what comes out of
//     the cropper is staged, so what the admin framed is what every user sees, and the bytes that
//     reach genesis are the cropper's 640 by 160, never the upload itself.
//   - REMOVAL IS STAGED, NOT WRITTEN. It becomes `logo: null` in the next save and is undone by
//     the SaveBar's discard, which is the confirmation step
//     @adr SPARK-0011-destructive-actions-require-confirmation@1 asks for: nothing reaches genesis
//     on the first click, and uploading again restores what was removed.
//
// GENESIS IS THE AUTHORITY ON WHAT AN IMAGE IS. It decides on the bytes (magic numbers, SVG refused)
// and enforces its inline ceiling on what the cropper produced; the size and type checks below only
// spare the admin a file that could never be framed, and a genesis refusal is surfaced as itself
// through the panel's save.

/** The state and the operations the Branding panel needs for the logo control. */
export interface BrandLogoEditing {
  /** What the control should DRAW right now, as an <img src>, or null for the empty state. */
  shown: string | null;
  /**
   * The raw file the cropper is holding, or null when no crop is in progress. Nothing is staged and
   * nothing is unsaved while this is set: a file becomes a pick only through {@link applyCrop}.
   */
  cropFile: File | null;
  /** True while a pick or a staged removal has not been saved: what the SaveBar reads. */
  dirty: boolean;
  /** A refusal of a chosen file, already localized, or null. */
  message: string | null;
  /** Refuses the doomed files by name and opens the cropper on everything else. */
  choose: (files: FileList) => void;
  /** Stages what the admin framed as the pick. */
  applyCrop: (result: CropResult) => void;
  /** Closes the cropper and stages nothing. The previous logo, picked or stored, stands. */
  cancelCrop: () => void;
  /** Closes the cropper and says why, for a file the cropper itself could not use. */
  rejectCrop: (reason: AvatarRejection) => void;
  /** Stages the removal of the stored logo. Written as `logo: null` on the next save. */
  remove: () => void;
  /**
   * What the settings patch should carry for `logo`: the picked file, `null` for a staged removal,
   * `undefined` when the logo was not touched and the patch must not mention it.
   */
  patch: () => SystemSettingsFileInput | null | undefined;
  /** After a successful save and refetch: the stored value now IS the pick, so forget it. */
  settle: () => void;
  /** Drops the pick, an open cropper, the staged removal and any refusal. The stored logo stands. */
  discard: () => void;
  /**
   * genesis refused the pick: say so beside the control. The pick stays, so the admin sees what was
   * refused and can replace or discard it; the SaveBar carries the same sentence.
   */
  refuse: (message: string) => void;
}

export function useBrandLogoEditing(
  /** The stored logo as the settings query returned it, or null when none is set. */
  stored: ImageValue | null | undefined,
): BrandLogoEditing {
  const intl = useIntl();
  const [picked, setPicked] = useState<SystemSettingsFileInput | null>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [removed, setRemoved] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const storedSrc = imageDataUrl(stored);
  const shown = picked ? imageDataUrl(picked) : removed ? null : storedSrc;
  // A removal of nothing is not an edit: the SaveBar must not light up for a logo that was never
  // there, and the patch must not send a `null` genesis would merge as a no-op anyway.
  const dirty = picked !== null || (removed && storedSrc !== null);

  function choose(files: FileList) {
    const file = files[0];
    // A cancelled dialog and a drop that carried only a directory both arrive as an empty list.
    // That is neither a refusal nor a choice, so every piece of state stays exactly as it was.
    if (!file) return;
    // Both checks bite before the cropper mounts, so a file that could never be a logo is refused
    // by its own name rather than after the admin has spent a moment framing it.
    if (file.size > MAX_BRAND_LOGO_BYTES) {
      setMessage(
        intl.formatMessage(
          {
            id: "adminSettings.branding.logoTooLarge",
            defaultMessage: "{name} is too large. The logo may be at most {max, number} MB.",
          },
          { name: file.name, max: MAX_BRAND_LOGO_BYTES / MB },
        ),
      );
      return;
    }
    if (!acceptsFile(file, RASTER_IMAGE_ACCEPT)) {
      setMessage(
        intl.formatMessage(
          {
            id: "adminSettings.branding.logoUnsupported",
            defaultMessage:
              "{name} cannot be used as the logo. Choose a PNG, JPEG, GIF, BMP, TIFF or WebP image.",
          },
          { name: file.name },
        ),
      );
      return;
    }
    setMessage(null);
    setCropFile(file);
  }

  function applyCrop({ mimeType, content }: CropResult) {
    setCropFile(null);
    setMessage(null);
    setRemoved(false);
    // The cropper hands back a PNG whatever the upload was, so the stored extension says so too:
    // genesis keeps it beside the bytes and it is all a later reader has to go on.
    setPicked({ mimeType, fileExtension: mimeType === "image/png" ? "png" : "jpg", content });
  }

  function cancelCrop() {
    setCropFile(null);
  }

  function rejectCrop(reason: AvatarRejection) {
    // CLOSE FIRST, THEN SPEAK. The refusal is printed under the control, and a message left behind
    // an open modal is a message nobody reads.
    setCropFile(null);
    setMessage(
      reason === "pixels"
        ? intl.formatMessage(
            {
              id: "adminSettings.branding.logoTooManyPixels",
              defaultMessage: "This image is too large to crop. Use one under {max} megapixels.",
            },
            { max: Math.round(MAX_AVATAR_PIXELS / 1_000_000) },
          )
        : // A file that decodes to nothing is a renamed non-image or a corrupt one: the type check
          // above only ever saw what the browser CLAIMED the file was.
          intl.formatMessage({
            id: "adminSettings.branding.logoUnreadable",
            defaultMessage: "That file could not be read. Try choosing it again.",
          }),
    );
  }

  function remove() {
    setMessage(null);
    setPicked(null);
    setRemoved(true);
  }

  function patch(): SystemSettingsFileInput | null | undefined {
    if (picked) return picked;
    if (removed && storedSrc !== null) return null;
    return undefined;
  }

  function settle() {
    setPicked(null);
    setRemoved(false);
    setMessage(null);
  }

  function discard() {
    settle();
    // An open cropper is a chosen file that has not been staged yet, and it is exactly as unwanted
    // after a discard as the pick is. Leaving it would put the dialog back over a page that has
    // just thrown its edits away.
    setCropFile(null);
  }

  function refuse(refusal: string) {
    setMessage(refusal);
  }

  return {
    shown,
    cropFile,
    dirty,
    message,
    choose,
    applyCrop,
    cancelCrop,
    rejectCrop,
    remove,
    patch,
    settle,
    discard,
    refuse,
  };
}

/**
 * genesis's own refusal of the logo, localized, or null when the error is about something else.
 *
 * Matched by CODE, not by classification: the settings patch carries the app name and the palette
 * too, so a VALIDATION on this mutation is not necessarily about the image. The two codes are the
 * only ones the image gate raises (genesis#372); anything else keeps the generic path.
 */
export function brandLogoRefusal(intl: IntlShape, error: unknown): string | null {
  if (graphQlErrorExtensions(error, BRAND_IMAGE_TOO_LARGE)) {
    return intl.formatMessage({
      id: "adminSettings.branding.logoRefusedTooLarge",
      defaultMessage: "The logo was refused: the cropped image is still too large.",
    });
  }
  if (graphQlErrorExtensions(error, BRAND_IMAGE_UNSUPPORTED)) {
    return intl.formatMessage({
      id: "adminSettings.branding.logoRefusedUnsupported",
      defaultMessage: "The logo was refused: it is not a PNG, JPEG, GIF, BMP, TIFF or WebP image.",
    });
  }
  return null;
}
